import { ArrowLeft } from "lucide-react";
import type React from "react";
import { useLayoutEffect } from "react";

import { Button } from "@/web/components/primitives/button";
import { Card, CardContent } from "@/web/components/primitives/card";
import { Progress } from "@/web/components/primitives/progress";
import { Spinner } from "@/web/components/primitives/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/web/components/primitives/tooltip";
import { resetOnboardingStepView } from "@/web/lib/documentViewReset";
import { cn } from "@/web/lib/styling";

type StandardFormWrapperProps = {
  children: React.ReactNode;
  /**
   * Each mount resets scroll and blur. When set, an extra reset runs whenever this key
   * changes (sub-steps that reuse one wrapper). Top-level flow step changes mount a new
   * wrapper, so they do not need a parent `useLayoutEffect`.
   */
  stepScrollKey?: string;
  backAction?: () => void;
  isGoingBack?: boolean;
  continueAction?: () => void;
  continueLabel?: string;
  isSubmitting?: boolean;
  backDisabled?: boolean;
  backDisabledReason?: string;
  continueDisabled?: boolean;
  continueDisabledReason?: string;
  progress?: number;
  bottomContinueButton?: boolean;
  noContinueButton?: boolean;
  darkBackground?: boolean;
  allowSkip?: boolean;
  bottomSkipButton?: boolean;
  isSkipping?: boolean;
  skipAction?: () => void;
  noBottomButtonSpacer?: boolean;
  childrenParentClassName?: string;
  title?: string;
  titleClassName?: string;
  stepTitle?: string;
  dialogMode?: boolean;
};

export function StandardFormWrapper({
  children,
  stepScrollKey,
  backAction,
  isGoingBack = false,
  continueAction,
  continueLabel,
  isSubmitting,
  backDisabled,
  backDisabledReason,
  continueDisabled,
  continueDisabledReason,
  progress,
  bottomContinueButton = false,
  noContinueButton = false,
  darkBackground = false,
  allowSkip = false,
  bottomSkipButton = false,
  isSkipping = false,
  skipAction,
  noBottomButtonSpacer = false,
  childrenParentClassName,
  title,
  titleClassName,
  stepTitle,
  dialogMode = false,
}: StandardFormWrapperProps) {
  useLayoutEffect(() => {
    resetOnboardingStepView();
  }, [stepScrollKey]);

  return (
    <div className={cn("flex flex-col items-center gap-6", dialogMode && "w-full")}>
      <ProgressOrSpacer progress={progress} dialogMode={dialogMode} />
      <FormTopNav
        backAction={backAction}
        isGoingBack={isGoingBack}
        backDisabled={backDisabled}
        backDisabledReason={backDisabledReason}
        darkBackground={darkBackground}
        dialogMode={dialogMode}
        stepTitle={stepTitle}
        bottomContinueButton={bottomContinueButton}
        noContinueButton={noContinueButton}
        continueDisabled={continueDisabled}
        continueDisabledReason={continueDisabledReason}
        isSubmitting={isSubmitting}
        continueAction={continueAction}
        continueLabel={continueLabel}
        allowSkip={allowSkip}
        bottomSkipButton={bottomSkipButton}
        isSkipping={isSkipping}
        skipAction={skipAction}
      />
      <FormMainColumn
        title={title}
        titleClassName={titleClassName}
        dialogMode={dialogMode}
        childrenParentClassName={childrenParentClassName}
        noBottomButtonSpacer={noBottomButtonSpacer}
        bottomContinueButton={bottomContinueButton}
        bottomSkipButton={bottomSkipButton}
        noContinueButton={noContinueButton}
        continueDisabled={continueDisabled}
        continueDisabledReason={continueDisabledReason}
        isSubmitting={isSubmitting}
        continueAction={continueAction}
        continueLabel={continueLabel}
        allowSkip={allowSkip}
        isSkipping={isSkipping}
        skipAction={skipAction}
      >
        {children}
      </FormMainColumn>
    </div>
  );
}

function ProgressOrSpacer({ progress, dialogMode }: { progress?: number; dialogMode: boolean }) {
  if (progress !== undefined) {
    return (
      <div className="flex w-full justify-center">
        <Progress
          value={progress}
          className="w-full rounded-none bg-withus-blue/20"
          indicatorClassName="bg-withus-blue"
        />
      </div>
    );
  }
  if (dialogMode) return null;
  return <div className="flex h-2 w-full justify-center" />;
}

function FormTopNav({
  backAction,
  isGoingBack,
  backDisabled,
  backDisabledReason,
  darkBackground,
  dialogMode,
  stepTitle,
  bottomContinueButton,
  noContinueButton,
  continueDisabled,
  continueDisabledReason,
  isSubmitting,
  continueAction,
  continueLabel,
  allowSkip,
  bottomSkipButton,
  isSkipping,
  skipAction,
}: Pick<
  StandardFormWrapperProps,
  | "backAction"
  | "isGoingBack"
  | "backDisabled"
  | "backDisabledReason"
  | "darkBackground"
  | "dialogMode"
  | "stepTitle"
  | "bottomContinueButton"
  | "noContinueButton"
  | "continueDisabled"
  | "continueDisabledReason"
  | "isSubmitting"
  | "continueAction"
  | "continueLabel"
  | "allowSkip"
  | "bottomSkipButton"
  | "isSkipping"
  | "skipAction"
>) {
  return (
    <div className={cn("w-full px-0", !dialogMode && "md:px-6")}>
      <nav className={cn("mx-auto grid grid-cols-[auto_1fr_auto] items-center 2xl:max-w-8xl")}>
        <div className="flex justify-start">
          {backAction && (
            <BackButton
              onClick={backAction}
              disabled={backDisabled}
              tooltipContent={backDisabledReason}
              darkBackground={darkBackground}
              isGoingBack={isGoingBack}
              dialogMode={dialogMode}
            />
          )}
        </div>
        <div className="flex justify-center">
          {stepTitle && (
            <Card className="rounded-xl border-0 py-2 shadow-none">
              <CardContent className="px-5">
                <h2 className={cn("text-center text-sm")}>{stepTitle}</h2>
              </CardContent>
            </Card>
          )}
        </div>
        <div className="flex items-center justify-end gap-2">
          {!bottomContinueButton && !noContinueButton && (
            <ContinueButton
              continueDisabledReason={continueDisabledReason}
              continueDisabled={continueDisabled}
              isSubmitting={isSubmitting}
              continueAction={continueAction}
              continueLabel={continueLabel}
            />
          )}
          {allowSkip && !bottomSkipButton && (
            <Button
              variant="ghost"
              disabled={isSubmitting || isSkipping}
              type="button"
              onClick={skipAction}
              className="text-md w-full max-w-lg py-4 text-accent-foreground/70"
            >
              {isSkipping ? <Spinner className="text-accent-foreground/70" /> : "Skip"}
            </Button>
          )}
        </div>
      </nav>
    </div>
  );
}

function FormMainColumn({
  children,
  title,
  titleClassName,
  dialogMode,
  childrenParentClassName,
  noBottomButtonSpacer,
  bottomContinueButton,
  bottomSkipButton,
  noContinueButton,
  continueDisabled,
  continueDisabledReason,
  isSubmitting,
  continueAction,
  continueLabel,
  allowSkip,
  isSkipping,
  skipAction,
}: Pick<
  StandardFormWrapperProps,
  | "title"
  | "titleClassName"
  | "dialogMode"
  | "childrenParentClassName"
  | "noBottomButtonSpacer"
  | "bottomContinueButton"
  | "bottomSkipButton"
  | "noContinueButton"
  | "continueDisabled"
  | "continueDisabledReason"
  | "isSubmitting"
  | "continueAction"
  | "continueLabel"
  | "allowSkip"
  | "isSkipping"
  | "skipAction"
> & { children: React.ReactNode }) {
  return (
    <div
      className={cn(
        "flex w-full max-w-200 animate-in flex-col items-center gap-[4.21vh] px-4 duration-500 fade-in",
        dialogMode ? "px-0" : "md:px-6",
        dialogMode ? "h-full" : "",
        childrenParentClassName,
      )}
    >
      {title && (
        <h1
          className={cn(
            "text-center font-heading text-[24px] leading-none uppercase md:text-[40px]",
            titleClassName,
          )}
        >
          {title}
        </h1>
      )}
      {dialogMode ? (
        <div className="flex h-full w-full flex-col overflow-x-hidden overflow-y-auto">
          <div className="max-h-0">{children}</div>
        </div>
      ) : (
        children
      )}
      {!noBottomButtonSpacer && <div className="hidden h-1 w-full justify-center md:block" />}
      {(bottomContinueButton || bottomSkipButton) && (
        <div
          className={cn(
            "flex w-full max-w-150 flex-col justify-center gap-4 pb-4",
            dialogMode ? "pb-0" : "",
          )}
        >
          {bottomContinueButton && !noContinueButton && (
            <ContinueButton
              continueDisabledReason={continueDisabledReason}
              continueDisabled={continueDisabled}
              isSubmitting={isSubmitting}
              continueAction={continueAction}
              continueLabel={continueLabel}
              className="text-md w-full py-4"
            />
          )}
          {allowSkip && bottomSkipButton && (
            <Button
              variant="ghost"
              disabled={isSubmitting || isSkipping}
              type="button"
              onClick={skipAction}
              className="text-md w-full py-4"
            >
              {isSkipping ? <Spinner className="text-accent-foreground/70" /> : "I'm not sure"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function BackButton({
  onClick,
  disabled,
  tooltipContent,
  darkBackground,
  isGoingBack,
  dialogMode,
}: {
  onClick: () => void;
  disabled?: boolean;
  tooltipContent?: string;
  darkBackground?: boolean;
  isGoingBack?: boolean;
  dialogMode?: boolean;
}) {
  const btn = (
    <Button
      variant="ghost"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        dialogMode ? "p-0" : undefined,
        darkBackground && "text-white hover:text-white/80",
      )}
    >
      {isGoingBack ? <Spinner /> : <ArrowLeft className={cn(dialogMode ? "size-4" : "size-6")} />}
    </Button>
  );

  if (!tooltipContent) return btn;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">{btn}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[40ch] text-wrap">{tooltipContent}</TooltipContent>
    </Tooltip>
  );
}

function ContinueButton({
  continueDisabledReason,
  continueDisabled,
  isSubmitting,
  continueAction,
  continueLabel,
  className,
}: {
  continueDisabledReason?: string;
  continueDisabled?: boolean;
  isSubmitting?: boolean;
  continueAction?: () => void;
  continueLabel?: string;
  className?: string;
}) {
  const btn = (
    <Button
      disabled={continueDisabled}
      isLoading={isSubmitting}
      onClick={continueAction}
      color="blue"
      className={className}
    >
      {continueLabel ?? "Continue"}
    </Button>
  );

  if (!continueDisabledReason) return btn;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">{btn}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[40ch] text-wrap" hideArrow>
        {continueDisabledReason}
      </TooltipContent>
    </Tooltip>
  );
}
